import type { CharacterDef } from '../characters/types';
import type { Piece } from './types';
import type { SpellId } from './types';

/**
 * What each spell costs its caster, in spell points.
 *
 * The scale is the balance: a mage starts with twelve, so it opens with two cheap spells or one
 * expensive one and then has to hold a power point to do it again. That is what makes the ringed
 * squares worth standing on beyond the victory they count towards, and it is why no spell needs a
 * separate once-per-game flag — a costly spell is already once per recharge.
 *
 * Shift Time is not a spell. It is the player choosing to let the round pass, so it is free, it is
 * always available, and it sits apart from the others on the tablet.
 */
export const SPELL_COSTS: Readonly<Record<SpellId, number>> = Object.freeze({
  'shift-time': 0,
  heal: 2,
  teleport: 3,
  exchange: 3,
  release: 3,
  curse: 4,
  freeze: 4,
  revive: 6,
  'summon-elemental': 8,
  /**
   * The dearest thing a caster can do, and deliberately dearer than summoning a whole elemental.
   * Imprison takes a piece out of the game until the cycle turns, which the caster does not
   * control and may be a long time; at six it was cheap enough that the search preferred it to a
   * winning capture. At nine a mage spends three quarters of everything it holds, and then stands
   * on a ring for nine turns to do it again.
   */
  imprison: 9,
});

/** Whether a character knows a spell at all, whatever it can currently afford. */
export function knowsSpell(character: CharacterDef | undefined, spell: SpellId): boolean {
  if (spell === 'shift-time') return true;
  return character?.spells?.includes(spell) ?? false;
}

/** Whether a piece can pay for a spell it knows. */
export function canAfford(piece: Piece, spell: SpellId): boolean {
  return piece.spellPoints >= SPELL_COSTS[spell];
}

/** The most magic a character can hold; 0 for everyone who is not a magician. */
export function maxSpellPoints(character: CharacterDef | undefined): number {
  return character?.spellPoints ?? 0;
}

/** Whether a piece can cast anything at all right now, ignoring targets. */
export function hasCastableSpell(character: CharacterDef | undefined, piece: Piece): boolean {
  return (character?.spells ?? []).some((spell) => canAfford(piece, spell));
}
